import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { PermissionGate } from './PermissionGate';

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  permission?: { resource: string; action: string };
  disabled?: boolean;
}

export interface ActionMenuSection {
  title?: string;
  items: ActionMenuItem[];
}

interface ActionMenuProps {
  sections: ActionMenuSection[];
}

interface DropdownPosition {
  top: number;
  left: number;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ sections }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 208; // w-52 = 13rem = 208px
      const dropdownHeight = 200; // Approximate max height

      let top = rect.bottom + 4; // 4px gap below button
      let left = rect.right - dropdownWidth; // Align right edge with button

      // Ensure dropdown doesn't go off the right edge
      if (left < 8) {
        left = 8;
      }

      // Ensure dropdown doesn't go off the bottom edge
      if (top + dropdownHeight > window.innerHeight) {
        top = rect.top - dropdownHeight - 4; // Position above button
      }

      setPosition({ top, left });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();

      // Close on scroll
      const handleScroll = () => setIsOpen(false);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const renderMenuItem = (item: ActionMenuItem) => {
    const button = (
      <button
        key={item.label}
        onClick={() => {
          if (!item.disabled) {
            item.onClick();
            setIsOpen(false);
          }
        }}
        disabled={item.disabled}
        className={`flex items-center w-full text-left px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed ${
          item.variant === 'danger' ? 'text-red-600' : 'text-gray-700'
        }`}
      >
        {item.icon && <span className="mr-2 h-4 w-4">{item.icon}</span>}
        {item.label}
      </button>
    );

    if (item.permission) {
      return (
        <PermissionGate
          key={item.label}
          resource={item.permission.resource}
          action={item.permission.action}
        >
          {button}
        </PermissionGate>
      );
    }

    return button;
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed w-52 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
      style={{ top: position.top, left: position.left }}
    >
      <div className="py-1">
        {sections.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {sectionIndex > 0 && (
              <div className="border-t border-gray-100 my-1" />
            )}
            {section.title && (
              <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase">
                {section.title}
              </div>
            )}
            {section.items.map(renderMenuItem)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label="More actions"
      >
        <MoreVertical className="h-5 w-5 text-gray-500" />
      </button>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default ActionMenu;
